import dayjs from "dayjs";
import { countLeafColumns } from "src/generator/PortraitOrientation/PortraitSection/PortraitSectionHeaders/generateHeaders.utils";
import { type ICol } from "src/interfaces";
import { type IReportEntry } from "src/pages/Reports/Report/Report.types";
import {
  type IChart,
  type ILogbookCharts,
} from "src/redux/slices/entriesGeneratorSlice";

import { hasFilter, resolveValueSource } from "../Charts/Chart.utils";

// Mirrors the unwrap Section.tsx/Report.tsx apply before rendering/exporting:
// section.headers can arrive wrapped in an extra array level.
export const getWorkingHeaders = (headers: unknown): ICol[] => {
  if (!headers) return [];
  return Array.isArray((headers as any)[0])
    ? (headers as any)[0]
    : (headers as ICol[]);
};

// Mirrors the same normalization Report.tsx applies to section.rows before
// export: rows can arrive double-wrapped (sub-row groups) and need flattening.
export const getNormalizedRows = (rows: unknown): any[] => {
  if (!rows || !Array.isArray(rows)) return [];
  const isSubRows = (rows as any)[0]?.[0] && Array.isArray((rows as any)[0][0]);
  return isSubRows ? (rows as any[]).flat() : (rows as any[]);
};

// Mirrors Row.tsx:49 — a row is either { cols: ICol[] } or a bare ICol[].
export const getRowCols = (row: unknown): ICol[] => {
  if (!row) return [];
  return "cols" in (row as any) ? (row as any).cols : (row as ICol[]);
};

// Resolves a leaf column's name to its position in the leaf-column-ordered
// row cell array, walking nested group headers the same way
// generateHeaders.utils.ts's countLeafColumns/calculateHeaderDepth do, since
// a group header occupies one slot in `headers` but spans N leaf columns.
export const findLeafColumnIndex = (
  headers: ICol[],
  colName: string,
): number => {
  let offset = 0;

  for (const header of headers) {
    if (header.type === "space") {
      if (header.name === colName) return offset;
      offset += 1;
      continue;
    }

    if (header.cols && header.cols.length > 0) {
      const nested = findLeafColumnIndex(header.cols, colName);
      if (nested !== -1) return offset + nested;
      offset += countLeafColumns(header.cols);
      continue;
    }

    if (header.name === colName) return offset;
    offset += 1;
  }

  return -1;
};

// Same order as findLeafColumnIndex.
export const getLeafColumns = (headers: ICol[]): ICol[] =>
  headers.flatMap(header =>
    header.type !== "space" && header.cols && header.cols.length > 0
      ? getLeafColumns(header.cols)
      : [header],
  );

export const findLeafColumn = (
  headers: ICol[],
  colName: string,
): ICol | undefined => {
  for (const header of headers) {
    if (header.type === "space") {
      if (header.name === colName) return header;
      continue;
    }

    if (header.cols && header.cols.length > 0) {
      const nested = findLeafColumn(header.cols, colName);
      if (nested) return nested;
      continue;
    }

    if (header.name === colName) return header;
  }

  return undefined;
};

// Mirrors Row.tsx:79-88's colSpan accounting, so a leaf index still resolves
// to the correct cell when earlier row cells span more than one column.
export const getCellAtLeafIndex = (
  rowCols: ICol[],
  leafIndex: number,
): ICol | undefined => {
  let offset = 0;

  for (const cell of rowCols) {
    const cellAny = cell as any;
    const span = cellAny.colspan
      ? cellAny.colspan
      : cellAny.span
        ? 1 + cellAny.span
        : !cellAny.full
          ? 1
          : 999;

    if (leafIndex >= offset && leafIndex < offset + span) return cell;
    offset += span;
  }

  return undefined;
};

// The local time shown in the entry's title, without a zone.
export const getEntryLocalDate = (
  entry: Pick<
    IReportEntry,
    "LTDateTime" | "entryDate" | "datecreated" | "datecreatedOffset"
  >,
) => {
  const local = entry.LTDateTime ?? entry.entryDate;
  const offset = Number(entry.datecreatedOffset);
  const date =
    typeof local === "string" && dayjs(local).isValid()
      ? dayjs(local)
      : dayjs(entry.datecreated).add(isFinite(offset) ? offset : 0, "hours");
  return date.format("YYYY-MM-DDTHH:mm:ss");
};

// Mirrors Report.utils.tsx's getColValue default case.
export const extractCellValue = (col?: ICol): string | number | undefined => {
  if (!col) return undefined;
  const raw = col.value;
  const value = raw && typeof raw === "object" ? raw.value : raw;
  if (value === undefined || value === null || value === "") return undefined;
  return value;
};

// Existing charts keep their order; new ones follow.
const keepOrder = (charts: IChart[], prev: IChart[] = []) => {
  const position = new Map(prev.map((chart, index) => [chart.id, index]));
  return charts
    .map((chart, index) => ({ chart, index }))
    .sort(
      (a, b) =>
        (position.get(a.chart.id) ?? prev.length + a.index) -
        (position.get(b.chart.id) ?? prev.length + b.index),
    )
    .map(({ chart }) => chart);
};

export const moveChart = (charts: IChart[], id: string, targetId: string) => {
  const from = charts.findIndex(chart => chart.id === id);
  const to = charts.findIndex(chart => chart.id === targetId);
  if (from === -1 || to === -1 || from === to) return charts;
  const next = [...charts];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

export const mergeChartSettings = (
  rebuilt: ILogbookCharts[],
  prev: ILogbookCharts[],
): ILogbookCharts[] =>
  rebuilt
    .map(log => {
      const prevLog = prev.find(p => p.lbid === log.lbid);
      return {
        ...log,
        data: log.data.flatMap(freshChart => {
          const prevCharts =
            prevLog?.data.filter(
              p =>
                p.name === freshChart.name &&
                p.sectionIndex === freshChart.sectionIndex,
            ) ?? [];
          if (!prevCharts.length) return [freshChart];

          return prevCharts.map(prevChart => {
            const { valueSource } = prevChart;
            const isSelected = (column: string) =>
              log.data.some(
                c =>
                  c.name === column &&
                  c.sectionIndex === freshChart.sectionIndex,
              );
            const sumColumnGone =
              valueSource?.kind === "sum" &&
              !log.data.some(
                c =>
                  c.name === valueSource.column &&
                  c.sectionIndex === freshChart.sectionIndex,
              );
            return {
              ...freshChart,
              id: prevChart.id,
              type: prevChart.type,
              trendline: prevChart.trendline,
              sortBy: prevChart.sortBy,
              sortDirection: prevChart.sortDirection,
              groupSmall: prevChart.groupSmall,
              valueOnly: prevChart.valueOnly,
              title: prevChart.title,
              filter:
                prevChart.filter && isSelected(prevChart.filter.column)
                  ? prevChart.filter
                  : undefined,
              valueSource: sumColumnGone
                ? { kind: "count" as const }
                : valueSource,
            };
          });
        }),
      };
    })
    .map(log => ({
      ...log,
      data: keepOrder(log.data, prev.find(p => p.lbid === log.lbid)?.data),
    }));

// Pairs a chart with the columns its sum and filter read from.
export const finalizeChart = (original: IChart, charts: IChart[]): IChart => {
  const findColumn = (name: string) =>
    charts.find(
      candidate =>
        candidate.name === name &&
        candidate.sectionIndex === original.sectionIndex,
    );
  const filterSource = original.filter
    ? findColumn(original.filter.column)
    : undefined;
  const chart: IChart = {
    ...original,
    filter: filterSource ? original.filter : undefined,
    filterVals:
      filterSource && filterSource.name !== original.name && hasFilter(original)
        ? [...filterSource.rawVals]
        : undefined,
  };

  const valueSource = resolveValueSource(chart);
  if (valueSource.kind !== "sum")
    return { ...chart, valueSource, sumVals: undefined };
  const source = findColumn(valueSource.column);
  if (!source)
    return { ...chart, valueSource: { kind: "count" }, sumVals: undefined };
  return { ...chart, valueSource, sumVals: [...source.rawVals] };
};

// A column's last chart is kept as a value-only source instead of removed.
export const removeChart = (charts: IChart[], id: string): IChart[] => {
  const target = charts.find(chart => chart.id === id);
  if (!target) return charts;
  const isLast = !charts.some(
    chart =>
      chart.id !== id &&
      chart.sectionIndex === target.sectionIndex &&
      chart.name === target.name,
  );
  if (!isLast) return charts.filter(chart => chart.id !== id);
  return charts.map(chart =>
    chart.id === id
      ? {
          ...chart,
          valueOnly: true,
          valueSource: undefined,
          sumVals: undefined,
          filter: undefined,
          filterVals: undefined,
          trendline: false,
        }
      : chart,
  );
};
